/**
 * Product name display utilities.
 *
 * display_name is a deprecated Glide-era artifact — these functions
 * never read it. All display logic resolves from product_name and
 * vendor_product_name only.
 *
 * Context rules:
 *  - Customer-facing (invoice/estimate): line snapshot, then the linked product
 *  - Vendor-facing (PO): product_name primary, vendor_product_name subtitle
 *  - Internal (inventory): product_name primary, vendor_product_name subtitle
 */

/** Minimal product shape accepted by display helpers. */
interface ProductLike {
  product_name: string | null;
  vendor_product_name?: string | null;
}

/**
 * Primary display name for a product row (gl_products).
 * Falls through: product_name → vendor_product_name → fallback.
 */
export function getProductDisplayName(product: ProductLike, fallback = "Unnamed Product"): string {
  return product.product_name || product.vendor_product_name || fallback;
}

/**
 * Vendor subtitle for dual-name display (PO / inventory views).
 * Returns vendor_product_name when it differs from product_name,
 * null otherwise.
 */
export function getProductSubtitle(product: ProductLike): string | null {
  if (
    product.vendor_product_name &&
    product.product_name &&
    product.vendor_product_name !== product.product_name
  ) {
    return product.vendor_product_name;
  }
  return null;
}

/**
 * Minimal line-item shape (gl_invoice_lines / gl_estimate_lines), with the
 * joined product when the query asked for it. Every field is optional so the
 * PDF payload shapes — which model each column as optional — can pass through
 * unchanged.
 */
interface LineItemLike {
  gl_products?: {
    product_name?: string | null;
    vendor_product_name?: string | null;
  } | null;
  product_name?: string | null;
}

/**
 * Display name for a line item, resolved across the three columns that can
 * carry one. The precedence is not arbitrary:
 *
 * 1. `product_name` on the line — a point-in-time snapshot of the name the
 *    product was sold under. Most lines leave it blank, but where it is set it
 *    often no longer matches the product's current name, so it must win: an
 *    issued document should keep reading the way it read when it was issued.
 * 2. `gl_products.product_name` — the product's current (renamed) name.
 * 3. `gl_products.vendor_product_name` — the original vendor label, and the
 *    only name a product that was never renamed has.
 *
 * Returns null when no column carries a name, leaving the absent-value
 * treatment to the caller (a link label wants `undefined`, a cell wants a
 * dash).
 */
export function getLineItemProductName(lineItem: LineItemLike): string | null {
  const candidates = [
    lineItem.product_name,
    lineItem.gl_products?.product_name,
    lineItem.gl_products?.vendor_product_name,
  ];

  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) {
      return trimmed;
    }
  }

  return null;
}
